//frontend/src/components/dashboard/UserSettings.js
import React, { useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Paper,
    Grid,
    Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axios';
import { useTranslation } from 'react-i18next';


const UserSettings = ({ user }) => {
    const [formData, setFormData] = useState({
        email: user.email || '',
        phone: user.phone || '',
        telegram: user.telegram || '',
        instagram: user.instagram || ''
    });

    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
    });

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handlePasswordChange = (e) => {
        setPasswordData({
            ...passwordData,
            [e.target.name]: e.target.value
        });
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        try {
            await axiosInstance.patch('/auth/profile', formData);
            setSuccess('Profile updated successfully');
        } catch (err) {
            setError(err.response?.data?.message || 'Error updating profile');
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
    
        if (passwordData.newPassword !== passwordData.confirmNewPassword) {
            setError('New passwords do not match');
            return;
        }
    
        try {
            const response = await axiosInstance.patch('/auth/change-password', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            });
    
            // Сохраняем новый токен
            if (response.data.token) {
                localStorage.setItem('token', response.data.token);
            }
    
            setSuccess('Password updated successfully');
            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmNewPassword: ''
            });
        } catch (err) {
            setError(err.response?.data?.message || 'Error changing password');
        }
    };

    const handleDeleteAccount = async () => {
        try {
            await axiosInstance.delete('/auth/profile');
            localStorage.removeItem('token');
            navigate('/login');
        } catch (err) {
            setError(err.response?.data?.message || 'Error deleting account');
            setDeleteDialogOpen(false);
        }
    };

    return (
        <Paper sx={{ p: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Grid container spacing={3}>
                {/* Profile Settings */}
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                    {t('common.profilesettings')}
                    </Typography>
                    <Box component="form" onSubmit={handleProfileSubmit}>
                        <TextField
                            fullWidth
                            label="Email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            margin="normal"
                            type="email"
                            required
                        />
                        <TextField
                            fullWidth
                            label={t('common.phone')}
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            margin="normal"
                            required
                        />
                        <TextField
                            fullWidth
                            label="Telegram"
                            name="telegram"
                            value={formData.telegram}
                            onChange={handleInputChange}
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            label="Instagram"
                            name="instagram"
                            value={formData.instagram}
                            onChange={handleInputChange}
                            margin="normal"
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            sx={{ mt: 2 }}
                        >
                            {t('common.saveprofilechanges')}
                        </Button>
                    </Box>
                </Grid>

                {/* Password Change */}
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                    {t('common.changepassword')}
                    </Typography>
                    <Box component="form" onSubmit={handlePasswordSubmit}>
                        <TextField
                            fullWidth
                            label={t('common.currentpassword')}
                            name="currentPassword"
                            type="password"
                            value={passwordData.currentPassword}
                            onChange={handlePasswordChange}
                            margin="normal"
                            required
                        />
                        <TextField
                            fullWidth
                            label={t('common.newpassword')}
                            name="newPassword"
                            type="password"
                            value={passwordData.newPassword}
                            onChange={handlePasswordChange}
                            margin="normal"
                            required
                        />
                        <TextField
                            fullWidth
                            label={t('common.confirmnewpassword')}
                            name="confirmNewPassword"
                            type="password"
                            value={passwordData.confirmNewPassword}
                            onChange={handlePasswordChange}
                            margin="normal"
                            required
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            sx={{ mt: 2 }}
                        >
                            {t('common.changepassword')}
                        </Button>
                    </Box>
                </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* Delete Account */}
            <Box sx={{ mt: 3 }}>
                <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setDeleteDialogOpen(true)}
                >
                    {t('common.deleteaccount')}
                </Button>
            </Box>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Delete Account</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete your account? This action cannot be undone.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteAccount} color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default UserSettings;